import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getCandidateCentres, getCandidateSettingsDataThunk, getCandidatesJobsOverCentresThunk, submitCandidateNotificationSettings } from './thunks';
import { CandidateCentre, ICandidateJobsState, IPaginatedData, TCandidateAcceptedJob, TCandidateHistoryJob, TCandidateJob, TCandidatePendingJob, TCentreCandidatesJobs } from './types';


const initialState: ICandidateJobsState = {
    initial: null,
    error: '',
    inProgress: false,
    saveSettingProgress: false,
    candidateCentres: [],
    total_pending_jobs: 0,
    total_accepted_jobs: 0,
    candidateJobsList: null,
    candidatePendingJobsList: null,
    candidateAcceptedJobsList: null,
    candidateHistoryJobsList: null,
    candidateCalendarData: null,
    calendarDates: [],
    candidatesJobsDetailsOverCentres: null,
    candidateDayCalendarData: null,
    pendingJobCount:  0,
    acceptedJobCount: 0,
    candidateNotificationSettings: null,
    maxDistanceAwayForMyCalendar: 0,
    hasSimilarJobs: false,
    similar_jobs_start_index: null,
    expired_docs_start_index: 0,

}

const candidateJobs = createSlice({
    name: 'candidateJobs',
    initialState,
    reducers: {
        setCandidateJobsList: (state, action: PayloadAction<TCandidateJob>) => {

            const similarJobIndex = action.payload.similar_jobs_start_index;
            state.candidateJobsList = action.payload
            state.hasSimilarJobs = action.payload.has_similar_jobs || false;
            state.similar_jobs_start_index = similarJobIndex === 0 ? 0 : similarJobIndex || null;
            state.expired_docs_start_index = action.payload.expired_docs_start_index || 0;
        },
        setCandidatePendingJobsList: (state, action: PayloadAction<TCandidatePendingJob>) => {
            state.candidatePendingJobsList = action.payload
        },
        setCandidateAcceptedJobsList: (state, action: PayloadAction<TCandidateAcceptedJob>) => {
            state.candidateAcceptedJobsList = action.payload
        },
        setCandidateHistoryJobsList: (state, action: PayloadAction<TCandidateHistoryJob>) => {
            state.candidateHistoryJobsList = action.payload
        },
        setCandidateCalendarData: (state, action: PayloadAction<any>) => {
          const updatedPayload: Record<string, any> = {};
          const datesArray: { date: string, accepted_count: number, listing_count: number, availability: [] }[] = [];

          for (const weekKey in action.payload) {
            // Skip non-week keys like 'token'
            if (weekKey === "token") continue;
            if (weekKey === "total_accepted_jobs") {
              state.total_accepted_jobs = action.payload[weekKey];
              continue;
            }
            if (weekKey === "total_pending_jobs") {
              state.total_pending_jobs = action.payload[weekKey];
              continue;
            }

            const week = action.payload[weekKey];
            const updatedWeek: Record<string, any> = {};

            for (const day in week) {
              const dayData = week[day];
              const availability = Array.isArray(dayData.availability)
                ? dayData.availability
                : [];

              let checked: boolean | null = false;

              if (availability.some((a: any) => a.all_day_available === 2)) {
                checked = true; // For <ACC-630></ACC-630>
              } else if (
                availability.some((a: any) => a.all_day_available === 1)
              ) {
                checked = true;
              } else {
                checked = false;
              }

              // Push the date to the array
              if (dayData.date) {
                datesArray.push({
                  date: dayData.date,
                  accepted_count: dayData?.accepted_count,
                  listing_count: dayData?.listing_count,
                  availability: dayData?.availability || [],
                });
              }

              updatedWeek[day] = {
                ...dayData,
                checked,
              };
            }

            updatedPayload[weekKey] = updatedWeek;
          }
          state.candidateCalendarData = updatedPayload;
          state.pendingJobCount = action.payload.total_pending_jobs || 0;
          state.acceptedJobCount = action.payload.total_accepted_jobs || 0;
          state.calendarDates = datesArray;
        },
        setCandidateDayCalendarData: (state, action: PayloadAction<any>) => {
          state.candidateDayCalendarData = action.payload;
        },
        resetCandidateDayCalendarData: (state) => {
          state.candidateDayCalendarData = null;
        },
        setCandidatesJobDetailsOverCantresListAction: (state, action: PayloadAction<IPaginatedData<TCentreCandidatesJobs>>) => {
            state.candidatesJobsDetailsOverCentres = action.payload
        },
        setCandidateNotificationSettings: (state, action: PayloadAction<any>) => {
          state.candidateNotificationSettings = action.payload;
        },
        setMaxDistanceAwayForMyCalendarAction: (state, action: PayloadAction<number>) => {
          state.maxDistanceAwayForMyCalendar = action.payload;
        }
    },
    extraReducers: (builder) => {
      builder.addCase(
        getCandidatesJobsOverCentresThunk.pending,
        (state, action) => {
          state.inProgress = true;
          state.error = null;
        }
      );
      builder.addCase(
        getCandidatesJobsOverCentresThunk.fulfilled,
        (state, action) => {
          state.inProgress = false;
        }
      );
      builder.addCase(
        getCandidatesJobsOverCentresThunk.rejected,
        (state, action) => {
          state.inProgress = false;
          state.error = action.payload;
        }
      );

      builder.addCase(
        getCandidateSettingsDataThunk.pending,
        (state, action) => {
          state.inProgress = true;
          state.error = null;
        }
      );
      builder.addCase(
        getCandidateSettingsDataThunk.fulfilled,
        (state, action) => {
          state.inProgress = false;
        }
      );
      builder.addCase(
        getCandidateSettingsDataThunk.rejected,
        (state, action) => {
          state.inProgress = false;
          state.error = action.payload;
        }
      );

      builder.addCase(
        submitCandidateNotificationSettings.pending,
        (state, action) => {
          state.saveSettingProgress = true;
          state.error = null;
        }
      );
      builder.addCase(
        submitCandidateNotificationSettings.fulfilled,
        (state, action) => {
          state.saveSettingProgress = false;
        }
      );
      builder.addCase(
        submitCandidateNotificationSettings.rejected,
        (state, action) => {
          state.saveSettingProgress = false;
          state.error = action.payload;
        }
      );
      builder.addCase(getCandidateCentres.fulfilled, (state, action) => {
        const rawData = action.payload?.data || {};

        state.candidateCentres = Object.entries(rawData)
          .filter(
            ([key, value]) => key !== "token" && typeof value === "object"
          )
          .map(([_, value]) => value as CandidateCentre);
      });
    }
});

export const {
    setCandidateJobsList,
    setCandidatePendingJobsList,
    setCandidateAcceptedJobsList,
    setCandidateHistoryJobsList,
    setCandidateCalendarData,
    setCandidatesJobDetailsOverCantresListAction,
    setCandidateDayCalendarData,
    resetCandidateDayCalendarData,
    setCandidateNotificationSettings,
    setMaxDistanceAwayForMyCalendarAction
} = candidateJobs.actions

export default candidateJobs.reducer;